import { K8sResourceCommon } from './k8s';

export enum SecretForComponentOption {
  none = 'none',
  all = 'all',
  partial = 'partial',
}

export const SecretByUILabel = 'ui.appstudio.redhat.com/secret-for';

export type CurrentComponentRef = {
  componentName: null | string;
  applicationName: null | string;
};

export type ImportSecret = {
  secretName: string;
  type: string;
  source?: Source;
  opaque?: {
    keyValues: {
      key: string;
      value: string;
      readOnlyKey?: boolean;
    }[];
  };
  image?: Image;
  labels?: KeyValueEntry[];
  currentComponent?: null | CurrentComponentRef;
  relatedComponents?: [];
  secretForComponentOption?: null | SecretForComponentOption;
};

export enum SecretSPILabel {
  ENVIRONMENT = 'appstudio.redhat.com/environment',
  APPLICATION = 'appstudio.redhat.com/application',
  COMPONENT = 'appstudio.openshift.io/component',
}

export enum SecretLabels {
  CREDENTIAL_LABEL = 'appstudio.redhat.com/credentials',
  CREDENTIAL_VALUE = 'scm',
  HOST_LABEL = 'appstudio.redhat.com/scm.host',
  REPO_ANNOTATION = 'appstudio.redhat.com/scm.repository',
  COMMON_SECRET_LABEL = 'build.appstudio.openshift.io/common-secret',
  /** Stores `Secret.type` (e.g. kubernetes.io/dockerconfigjson) for legacy secrets without `type` on metadata-only views. */
  K8S_TYPE_LABEL = 'appstudio.redhat.com/kubernetes-secret-type',
  /** When type is source: `basic` | `ssh` (disambiguates basic-auth vs ssh-auth without `data`). */
  SOURCE_AUTH_KIND_LABEL = 'appstudio.redhat.com/scm-auth-kind',
}

export enum TargetDropdownDefaults {
  ALL_ENVIRONMENTS = 'All environments',
  ALL_COMPONENTS = 'All components',
}

export enum SecretFor {
  Deployment = 'Deployment',
  Build = 'Build',
}

export type SecretKind = K8sResourceCommon & {
  data?: { [key: string]: string };
  stringData?: { [key: string]: string };
  type?: string;
};

type linkServiceAccount = {
  serviceAccount: {
    as?: 'secret' | 'imagePullSecret';
    managed?: {
      generateName?: string;
      name?: string;
    };
    reference: {
      name: string;
    };
  };
};

export type RemoteSecretKind = K8sResourceCommon & {
  spec: {
    secret: {
      linkedTo?: linkServiceAccount[];
      name: string;
      type: string;
      labels?: {
        [key: string]: string;
      };
      annotations?: {
        [key: string]: string;
      };
    };
  };
  status?: RemoteSecretStatus;
};

export interface RemoteSecretStatus {
  conditions?: SecretCondition[];
  secret?: Secret;
}

export enum RemoteSecretStatusReason {
  AwaitingData = 'AwaitingData',
  DataFound = 'DataFound',
  Injected = 'Injected',
  PartiallyInjected = 'PartiallyInjected',
  Error = 'Error',
  Unknown = '-',
}

export enum RemoteSecretStatusType {
  Deployed = 'Deployed',
  DataObtained = 'DataObtained',
}
export interface SecretCondition {
  lastTransitionTime: string;
  message: string;
  reason: RemoteSecretStatusReason | string;
  status: string;
  type: RemoteSecretStatusType | string;
}

export interface Secret {
  keys?: string[];
}

export interface Target {
  namespace: string;
  secretName: string;
}

export type BuildTimeSecret = {
  type: SecretType;
  name: string;
  providerUrl: string;
  tokenKeyName: string;
  source?: Source;
  opaque?: {
    keyValuePairs: {
      key: string;
      value: string;
      readOnlyKey?: boolean;
      readOnlyValue?: boolean;
    }[];
  };
  image?: Image;
  labels?: KeyValueEntry[];
};

export type SecretFormValues = ImportSecret & {
  existingSecrets?: BuildTimeSecret[];
};

export enum SecretTypeDropdownLabel {
  opaque = 'Key/value secret',
  image = 'Image pull secret',
  source = 'Source secret',
}

export enum SecretType {
  opaque = 'Opaque',
  dockerconfigjson = 'kubernetes.io/dockerconfigjson',
  basicAuth = 'kubernetes.io/basic-auth',
  dockercfg = 'kubernetes.io/dockercfg',
  serviceAccountToken = 'kubernetes.io/service-account-token',
  sshAuth = 'kubernetes.io/ssh-auth',
  tls = 'kubernetes.io/tls',
}

export enum LinkableSecretType {
  dockerconfigjson = 'kubernetes.io/dockerconfigjson',
  basicAuth = 'kubernetes.io/basic-auth',
  dockercfg = 'kubernetes.io/dockercfg',
}

export type ServiceAccountKind = {
  automountServiceAccountToken?: boolean;
  imagePullSecrets?: SecretKind[] | { [key: string]: string }[];
  secrets?: SecretKind[] | { [key: string]: string }[];
} & K8sResourceCommon;

export enum SecretTypeAbstraction {
  generic = 'generic',
  source = 'source',
  image = 'image',
}

export enum SecretTypeDisplayLabel {
  imagePull = 'Image pull',
  keyValue = 'Key/value',
  source = 'Source',
}

export enum ImagePullSecretType {
  ImageRegistryCreds = 'Image registry credentials',
  UploadConfigFile = 'Upload configuration file',
}

export enum SourceSecretType {
  basic = 'Basic authentication',
  ssh = 'SSH Key',
}
/**
 * For image pull, `ImageRegistryCreds` vs `UploadConfigFile` intentionally differ so callers
 * (e.g. edit secret) can tell manual registry creds from an uploaded config. The Kubernetes
 * Secret `type` for both when creating/updating normalized pull secrets is
 * `kubernetes.io/dockerconfigjson` — use `getKubernetesSecretType()` from secret-utils for that.
 */
export const K8sSecretType = {
  [SecretTypeDropdownLabel.opaque]: SecretType.opaque,
  [SecretTypeDropdownLabel.image]: SecretType.dockerconfigjson,
  [ImagePullSecretType.ImageRegistryCreds]: SecretType.dockerconfigjson,
  [ImagePullSecretType.UploadConfigFile]: SecretType.dockercfg,
  [SourceSecretType.basic]: SecretType.basicAuth,
  [SourceSecretType.ssh]: SecretType.sshAuth,
};

export interface AddSecretFormValues {
  type: string;
  name: string;
  secretFor: SecretFor;
  opaque: Opaque;
  image: Image;
  source: Source;
  labels?: KeyValueEntry[];
  relatedComponents?: string[];
  secretForComponentOption?: null | SecretForComponentOption;
}

export interface Image {
  authType: string;
  registryCreds?: RegistryCred[];
  dockerconfig?: string;
}

export interface RegistryCred {
  registry: string;
  username: string;
  password: string;
  email: string;
}

export type KeyValueEntry = {
  key: string;
  value: string;
  readOnlyKey?: boolean;
};
export interface Opaque {
  keyValues: KeyValueEntry[];
}

export interface Source {
  authType: string;
  username?: string;
  password?: string;
  host?: string;
  repo?: string;
}

export interface Targets {
  application: string;
  component: string;
}
